<template>
  <div :class="['content', programStyle]">
    <div v-if="!formDisplay && formTypes">
      <div class="upper flex-column">
        <div class="title">{{ $t('forms') }}</div>
        <div v-if="isFormTypeEmpty" class="no-form-types">
          <span>There are no forms available on this domain.</span>
        </div>
        <div v-else>
          <div class="flexRow">
            <div class="form-dropdowns flex-row">
              <Multiselect
                v-model="value"
                :options="formTypes"
                class="multiselect single"
                :placeholder="$t('select-option')"
                track-by="name"
                label="name"
                :showLabels="false"
                :allow-empty="false"
              ></Multiselect>
              <Multiselect
                v-if="showConfigSelect"
                v-model="config"
                :options="formConfigs"
                class="multiselect single formConfig"
                :placeholder="$t('select-option')"
                track-by="name"
                label="name"
                :custom-label="customLabel"
                :showLabels="false"
                :allow-empty="false"
              ></Multiselect>
            </div>
            <button
              v-if="this.value && this.value.name !== 'Survey-Interference'"
              :class="submitPermissions ? '' : 'disabled'"
              @click="newForm(config)"
            >
              {{ $t('submit-new') }}
            </button>
          </div>
          <div v-if="this.value.name !== 'Survey-Interference'" class="error-messages">
            <span v-if="this.value.name === 'Appeal' && !this.appealable"
              >There are no appealable dials for this dealership/entity.</span
            >
            <span v-if="this.formConfigs.length === 0">There are no active forms to submit in this program.</span>
            <ul v-if="this.value && this.config && !submitPermissions">
              <li v-if="this.config.existingConfigFormSubmitted">You have already submitted this form.</li>
              <li v-if="!this.value.hasPermission">You do not have permission to submit this form.</li>
            </ul>
          </div>
        </div>
      </div>
      <DataTable :headings="dealerFormsTH" :rows="rows" :perPage="rowCount" :page="currentPage">
        <template v-slot:statusSlot="slotProps">
          <div
            class="submitted"
            v-if="
              slotProps.row.formStepId === 'Dealer' &&
              slotProps.row.layout !== 'Survey-Interference' &&
              !slotProps.row.displayResults
            "
          >
            {{ $t('submitted') }}
          </div>
          <div
            class="in-review"
            v-else-if="
              slotProps.row.formStepId !== 'Dealer' &&
              slotProps.row.layout === 'Appeal' &&
              !slotProps.row.displayResults
            "
          >
            {{ $t('in-review') }}
          </div>
          <div class="completed" v-else-if="slotProps.row.displayResults && slotProps.row.layout === 'Appeal'">
            {{ $t('completed') }}
          </div>
          <div class="required" v-else-if="slotProps.row.result === 'Required'">
            {{ $t('must-acknowledge') }}
          </div>
          <div class="acknowledged" v-else-if="slotProps.row.result === 'Acknowledged'">
            {{ slotProps.row.result }}
          </div>
          <div
            v-if="slotProps.row.currentOverAllStatus === 'Approved' && slotProps.row.formType === 'Acknowledgement'"
            class="acknowledged"
          >
            {{ slotProps.row.currentOverAllStatus }}
          </div>
          <div
            v-if="slotProps.row.currentOverAllStatus === 'Declined' && slotProps.row.formType === 'Acknowledgement'"
            class="declined"
          >
            {{ slotProps.row.currentOverAllStatus }}
          </div>
        </template>
        <template v-slot:actionSlot="slotProps">
          <Eyecon class="eyecon" @click="viewForm(slotProps.row)"></Eyecon>
        </template>
      </DataTable>
      <div class="row-container" v-if="rows.length > 0">
        <div class="row-info">
          {{ $t('viewing-firstrow-lastrow-of-filteredassets-length', [firstRow, lastRow, rows.length]) }}
        </div>
        <BasePagination
          :current-page="currentPage"
          :page-count="pageCount"
          class="articles-list__pagination"
          @nextPage="pageChangeHandle('next')"
          @previousPage="pageChangeHandle('previous')"
          @loadPage="pageChangeHandle"
        />
        <div class="placeholder-div"></div>
      </div>
    </div>
    <div v-if="formDisplay">
      <router-view :appealType="appealType" @exit="clearForm" :config="config" />
    </div>
  </div>
</template>

<script>
import DataTable from '@/components/DataTable.vue';
import Multiselect from 'vue-multiselect';
import Eyecon from '@/assets/new/eye-con.svg';
import BasePagination from '@/components/BasePagination.vue';
import 'vue-multiselect/dist/vue-multiselect.min.css';
import { mapState } from 'vuex';
import dealerFormsTH from '@/utils/tableHeaders/dealerFormsTH';

export default {
  name: 'DealerForms',
  components: { Multiselect, DataTable, Eyecon, BasePagination },
  data() {
    return {
      appealResults: [],
      appealableDials: [],
      value: null,
      filterValue: null,
      rowCount: 10,
      currentPage: 1,
      rowCountDropValue: { label: this.$t('show-10'), value: 10 },
      firstRow: 1,
      lastRow: 10,
      formDisplay: null,
      appealType: null,
      formTypes: null,
      config: null,
      dealerFormsTH: dealerFormsTH(),
      rows: [],
      isFormTypeEmpty: null,
    };
  },
  methods: {
    getSubmittedForms() {
      const loader = this.$loading.show();

      this.formDisplay = null;
      this.config = null;

      const { entity } = this;
      let { domain } = this;

      // Survey Interference needs to be checked across all domains.
      // If Survey Interference is true, set that as the default form type.
      if (this.value && this.value.name === 'Survey-Interference') {
        domain = 'ALL';
      }
      const type = this.value.name;
      this.$store
        .dispatch('getSubmittedForms', { domain, entity, type })
        .then((res) => {
          this.appealResults = res;
          this.updateRows();
        })
        .catch((error) => {
          if (error.data) {
            this.$notify({
              group: 'primary',
              type: 'error',
              text: error.data.error,
            });
          }
        })
        .finally(() => {
          loader.hide();
        });
    },
    getAppealableDials() {
      const loader = this.$loading.show();

      const { entity, domain } = this;
      const { period } = this.config;

      if (this.value.name === 'Survey-Interference') {
        loader.hide();
        return;
      }
      this.$store.dispatch('getAppealableDials', { domain, entity, period }).then((res) => {
        this.appealableDials = res.appealable;
        this.$store.state.appealInfo = res;
        loader.hide();
      });
    },

    // If survey interference is true, default to that form type if it exists
    getFormTypesList() {
      const loader = this.$loading.show();

      const { domain, entity } = this;
      this.$store
        .dispatch('getFormTypesList', { domain, entity })
        .then((res) => {
          this.formTypes = res.types;
          this.isFormTypeEmpty = res.isFormTypeEmpty;
          if (this.formTypes.length > 0 && this.surveyInterference) {
            const index = this.formTypes.findIndex((types) => types.name === 'Survey-Interference');
            if (index !== -1 && this.surveyInterference) {
              this.value = this.formTypes[index];
            } else {
              this.value = this.formTypes[0];
            }
          } else {
            this.value = this.formTypes[0];
          }
          loader.hide();
        })
        .catch((error) => {
          if (error.data) {
            this.$notify({
              group: 'primary',
              type: 'error',
              text: error.data.error,
            });
          }
        })
        .finally(() => {
          loader.hide();
        });
    },
    newForm(config) {
      if (this.submitPermissions) {
        this.appealType = 'submit';
        this.formDisplay = config.layout;
        this.$store.state.selectedForm = this.appealableDials;
        if (config.layout === 'Appeal' && config.domain === 'US EL') {
          this.$router.push({
            name: 'CCAppeal',
          });
        } else {
          this.$router.push({
            name: config.layout,
          });
        }
      }
    },
    clearForm() {
      this.formDisplay = null;
      this.$store.state.otherAppealInfo = null;
      this.currentPage = 1;
      if (this.$route.name === 'Dealer-Forms') {
        this.getSubmittedForms();
        this.getFormTypesList();
      }
    },
    viewForm(form) {
      this.appealResults.forEach((appeal) => {
        if (appeal.formId === form.formId) {
          if (form.layout === 'Appeal') {
            appeal.items.forEach((dial) => this.$set(dial, 'dialResult', form.result));
          }
          if (form.domain === 'US EL') {
            appeal.items.forEach((item, index) => {
              if (item.dialTitle === appeal.dialDataArr[index].dialName) {
                this.$set(item, 'dialDataArr', [appeal.dialDataArr[index]]);
              }
            });
          }
          this.$store.state.selectedForm = appeal.items;
          this.$store.state.otherAppealInfo = appeal;
          this.formDisplay = this.value.name;
          this.appealType = 'view';
          if (form.layout) {
            if (form.layout === 'Appeal' && form.domain === 'US EL') {
              this.$store.state.selectedCCForm = appeal.dialDataArr;
              this.$router.push({
                name: 'CCAppeal',
              });
            } else {
              this.$router.push({
                name: form.layout,
              });
            }
          } else {
            this.$router.push({
              name: form.formType,
            });
          }
        }
      });
    },
    configSchedule(config) {
      let period = null;
      if (config.configType !== 'simple') {
        ({ period } = config);
      } else if (config.phases.length > 0) {
        const startDate = this.$options.filters.date(config.phases[0].startDate);
        const endDate = this.$options.filters.date(config.phases[0].endDate);
        period = `${startDate} - ${endDate}`;
      }
      return period;
    },
    isEmpty(obj) {
      if (obj) {
        return Object.keys(obj).length === 0;
      }
      return false;
    },
    async pageChangeHandle(value) {
      switch (value) {
        case 'next':
          this.currentPage += 1;
          break;
        case 'previous':
          this.currentPage -= 1;
          break;
        default:
          this.currentPage = value;
      }
      this.calcFirstAndLastRow();
    },
    calcFirstAndLastRow() {
      if (this.rows.length > 0) {
        this.firstRow = 1 + (this.currentPage - 1) * 10;
        if (this.rows.length < 10) {
          this.lastRow = this.rows.length;
        } else {
          this.lastRow = this.firstRow + 9;
        }
        if (this.lastRow > this.rows.length) {
          this.lastRow = this.rows.length;
        }
      }
    },
    updateRows() {
      this.rows = [];
      if (this.appealResults && this.value) {
        this.appealResults.forEach((result) => {
          if (result.statuses.length > 0) {
            if (this.value.name === 'Survey-Interference') {
              result.statuses[0].layout = 'Survey-Interference';
            } else if (!this.isEmpty(result.config)) {
              result.statuses[0].layout = result.config.layout;
              result.statuses[0].period = this.configSchedule(result.config);
              result.statuses[0].formType = result.config.formType;
              result.statuses[0].currentOverAllStatus = result.currentOverAllStatus;
              result.statuses[0].formName = result.config.name;
            } else {
              result.statuses[0].layout = result.layout;
            }
            if (result.displayResults === 'Y') {
              result.statuses[0].displayResults = true;
            } else {
              result.statuses[0].displayResults = false;
            }
            this.rows.push(result.statuses[0]);
          }
        });
      }
      this.calcFirstAndLastRow();
    },
    customLabel({ name, layout }) {
      return `${name} - ${layout}`;
    },
  },
  computed: {
    ...mapState('global', ['programStyle', 'programName']),
    ...mapState(['surveyInterference', 'appealable']),
    // In order to submit,
    // Appealable Dials need to exist
    // An active configuration needs to exist
    // A user needs to have permission
    // An active configuration needs to be selected
    // Selected active configuration should not have an existing/already submitted form
    submitPermissions() {
      if (
        this.value.configArray.length > 0 &&
        this.value.hasPermission &&
        this.config &&
        !this.config.existingConfigFormSubmitted
      ) {
        if (this.value.name !== 'Appeal') {
          return true;
        }
        if (this.value.name === 'Appeal' && this.appealableDials.length > 0) {
          return true;
        }
        return false;
      }
      return false;
    },
    entity() {
      return this.$route.params.entity;
    },
    domain() {
      return this.$route.params.domain;
    },
    pageCount() {
      const l = this.rows.length;
      const s = this.rowCountDropValue.value;
      return Math.ceil(l / s);
    },
    formConfigs() {
      if (this.value && !this.surveyInterference) {
        return this.value.configArray;
      }
      return [];
    },
    showConfigSelect() {
      if (this.formConfigs.length > 0) {
        if (this.value.name === 'Appeal') {
          return this.appealable;
        }

        return true;
      }
      return false;
    },
  },
  watch: {
    value() {
      this.getSubmittedForms();
    },
    config() {
      if (this.config && this.config.configType === 'multi-phase') {
        this.getAppealableDials();
      }
    },
  },
  created() {
    this.getFormTypesList();
  },
};
</script>

<style lang="scss" scoped>
.content {
  color: #000000;
  text-align: left;
  .link {
    color: #243882;
  }
}
.upper {
  padding: 40px;
  .flexRow {
    display: flex;
    align-items: center;
    justify-content: space-between;
    button {
      margin-left: 20px;
      width: 180px;
    }
  }
  .error-messages {
    margin-top: 15px;
    span {
      margin-left: 0px;
      display: block;
    }
    ul {
      padding-left: 20px;
      margin: 0px;
    }
  }
}
.title {
  margin-bottom: 25px;
  color: #243882;
  font-size: 21px;
}
.submitted {
  border: 1px solid #898c8d80;
  padding: 10px 0 10px 45px;
  width: 120px;
  display: flex;
  margin: auto;
}

.in-review {
  border: 1px solid #898c8d80;
  padding: 10px 0 10px 45px;
  width: 120px;
  display: flex;
  margin: auto;
  color: white;
  background: $fca-orange;
}
.completed {
  border: 1px solid #898c8d80;
  padding: 10px 0 10px 45px;
  width: 120px;
  display: flex;
  margin: auto;
  color: white;
  background: $fca-gray;
}

.declined {
  border: 1px solid #898c8d80;
  padding: 10px 0 10px 45px;
  width: 120px;
  display: flex;
  margin: auto;
  color: white;
  background: #ec0000;
}
.required {
  border: 1px solid #898c8d80;
  padding: 8px 10px 10px 10px;
  width: 120px;
  display: flex;
  margin: auto;
  color: white;
  background: #ffa734;
}
.acknowledged {
  border: 1px solid #898c8d80;
  padding: 10px 10px 10px 34px;
  width: 120px;
  display: flex;
  margin: auto;
  color: white;
  background: $fca-green;
}
.formConfig {
  margin-left: 15px;
}
</style>
